import React, { PureComponent } from 'react';
import styled from 'styled-components';

import { SearchSorting } from '@lt/components/icons-react/userflow';

import { SORT_TYPES, ORDER_ASC } from '../../constants';

import type { Field, SortingProps } from '../../types';
import { TEST_ID_SORTING_DESKTOP_SORT_TYPE_BTN } from './constants';
import CollapsedSelect from './components/CollapsedSelect/CollapsedSelect';

const SORT_ICON = <SearchSorting width={11} height="100%" />;

interface SortingState {
  isDesc: { [key in Field]?: boolean };
}

export default class Sorting extends PureComponent<SortingProps, SortingState> {
  /**
   * Переход к следующему типу сортировки в рамках одного вида сортировки
   * (i.e по убыванию -> по возрастанию)
   * @param {Array<Object>} types типы сортировки (i.e по-убыванию, по-возрастанию)
   * @param {String} currentOrder текущее направление сортировки
   */
  static getNextSortType(types, currentOrder) {
    const currIdx = types.findIndex((type) => type.order === currentOrder);
    const nextIdx = (currIdx + 1) % types.length;
    return types[nextIdx];
  }

  constructor(props) {
    super(props);

    this.changeSortingOrder = this.changeSortingOrder.bind(this);

    const { sortTypes = SORT_TYPES } = this.props;

    const isDesc = props.order === 'desc';

    const initialState = sortTypes.reduce(
      (acc, currentType) => ({
        ...acc,
        [currentType.id]: isDesc,
      }),
      {},
    );

    this.state = {
      isDesc: initialState as { [key in Field]?: boolean },
    };
  }

  changeSortingOrder(sortItem) {
    const { order = ORDER_ASC, field, changeSorting } = this.props;
    const { id, types } = sortItem;

    const type =
      field === id ? Sorting.getNextSortType(types, order) : types[0];

    const isDesc = type.order === 'desc';

    this.setState((prevState) => ({
      ...prevState,
      isDesc: {
        ...prevState.isDesc,
        [id]: isDesc,
      },
    }));

    return changeSorting({
      field: id,
      order: type.order,
    });
  }

  render() {
    const {
      field,
      order = ORDER_ASC,
      awaitFiltration,
      modifierStyles,
      isMapView,
      changeSorting,
      sortTypes = SORT_TYPES,
      className,
    } = this.props;

    return (
      <StyledContainer $isMapView={isMapView} className={className}>
        {isMapView && (
          <CollapsedSelect
            changeSorting={changeSorting}
            field={field}
            order={order}
          />
        )}
        {!isMapView &&
          sortTypes.map((sort) => {
            const { id, label, types } = sort;
            const { isDesc } = this.state;
            const isActive = id === field;
            const changeableOrder = types.length > 1;
            const clickAvailable =
              !awaitFiltration && (changeableOrder || !isActive);

            const showSortIcon = changeableOrder;

            return (
              <StyledButton
                type="button"
                key={id}
                onClick={
                  clickAvailable
                    ? () => this.changeSortingOrder(sort)
                    : undefined
                }
                isActive={isActive}
                isDesc={!!isDesc[id]}
                activeNoOrder={isActive && !changeableOrder}
                boldTextColor={modifierStyles?.boldTextColor}
                linkColor={modifierStyles?.linkColor}
                data-testid={`${TEST_ID_SORTING_DESKTOP_SORT_TYPE_BTN}_${id}`}
              >
                {label}
                {showSortIcon && SORT_ICON}
              </StyledButton>
            );
          })}
      </StyledContainer>
    );
  }
}

const StyledContainer = styled.div<{ $isMapView: boolean }>`
  display: flex;
  flex-wrap: wrap;
  margin-right: auto;

  ${({ $isMapView }) =>
    $isMapView &&
    `
    width: 320px;
    margin-right: unset;

    ${StyledButton} {
      &:first-child {
        width: 100%;
        text-align: left;
      }
    }
  `};
`;

const StyledButton = styled.button<{
  isActive: boolean;
  isDesc: boolean;
  activeNoOrder: boolean;
  boldTextColor?: string;
  linkColor?: string;
}>`
  display: flex;

  padding: 0;
  font-size: 16px;
  line-height: 20px;

  text-decoration: none;
  color: #3b88e2;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 8px 16px 8px 12px;
  border-radius: 40px;

  &::first-letter {
    text-transform: uppercase;
  }

  svg {
    width: 24px;
    height: 24px;
    padding: 7px 8px 7px 4px;
    margin-left: 4px;
    fill: #3b88e2;
  }

  ${({ isActive }) =>
    isActive &&
    ` color: #232323;
      background: white;
      svg {
        fill: #232323;
      }
    `}

  ${({ isActive, boldTextColor }) =>
    isActive &&
    boldTextColor &&
    `
      color: ${boldTextColor};
      svg {
        fill: ${boldTextColor};
      }
    `}

  ${({ isActive, linkColor }) =>
    !isActive &&
    linkColor &&
    `
      color: ${linkColor};
      svg {
        fill: ${linkColor};
      }
    `}

  ${({ isDesc }) => isDesc && `svg { transform: rotateX(180deg);}`}

  ${({ activeNoOrder }) => activeNoOrder && `pointer-events: none;`}
`;
